import React, { useEffect, useMemo, useState } from 'react';

const API_URL = (
  process.env.REACT_APP_API_URL ||
  'https://api.jmkinfosoft.com'
).replace(/\/$/, '');

const ITEMS_PER_PAGE = 20;

const QRSubmissions = ({
  qrList = [],
  onBack,
  autoSelectFirst = false
}) => {
  const [selectedQR, setSelectedQR] = useState(null);
  const [responses, setResponses] = useState([]);
  const [fields, setFields] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [responseSearch, setResponseSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ============================================
  // TOKEN
  // ============================================

  const getToken = () => {
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('feedbackToken') ||
      ''
    );
  };

  // ============================================
  // QR NAME
  // ============================================

  const getQRName = (qr) =>
    qr?.qr_name ||
    qr?.name ||
    qr?.office_name ||
    qr?.title ||
    'Unnamed QR';

  // ============================================
  // QR ID
  // ============================================

  const getQRId = (qr) =>
    qr?.qr_id ||
    qr?.id ||
    qr?.public_id;

  // ============================================
  // FIELD LABEL
  // ============================================

  const getFieldLabel = (field) => {
    if (field?.field_label) {
      return field.field_label;
    }

    if (field?.label) {
      return field.label;
    }

    if (field?.field_name) {
      return field.field_name
        .replace(/^f_/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) =>
          char.toUpperCase()
        );
    }

    return 'Field';
  };

  // ============================================
  // FIELD VALUE
  // ============================================

  const getFieldValue = (row, field) => {
    const fieldName = field?.field_name;

    if (!fieldName) {
      return '';
    }

    // Exact field name
    if (
      row?.[fieldName] !== undefined &&
      row?.[fieldName] !== null
    ) {
      return row[fieldName];
    }

    // Without f_ prefix
    const originalName =
      fieldName.replace(/^f_/, '');

    if (
      row?.[originalName] !== undefined &&
      row?.[originalName] !== null
    ) {
      return row[originalName];
    }

    return '';
  };

  // ============================================
  // FORMAT VALUE
  // ============================================

  const formatValue = (value, field) => {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return '-';
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '-';
      }
    }

    if (field?.field_type === 'rating') {
      return `${value}`;
    }

    return String(value);
  };

  // ============================================
  // SUBMISSION ID
  // ============================================

  const getSubmissionId = (row) =>
    row?.submission_id ||
    row?.submissionId ||
    row?.id ||
    '-';

  // ============================================
  // DATE
  // ============================================

  const formatDate = (value) => {
    if (!value) return '-';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ============================================
  // LOAD SUBMISSIONS
  // ============================================

  const loadSubmissions = async (qr) => {
    const qrId = getQRId(qr);

    if (!qrId) {
      setError('Invalid QR ID');
      return;
    }

    setSelectedQR(qr);
    setResponses([]);
    setFields([]);
    setLoading(true);
    setError('');
    setResponseSearch('');
    setCurrentPage(1);

    try {
      const token = getToken();

const response = await fetch(
  `${API_URL}/api/qr/${encodeURIComponent(qrId)}/submissions`,
  {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token
        ? { Authorization: `Bearer ${token}` }
        : {})
    }
  }
);

      const result = await response.json();

      console.log(
        '📥 QR submissions:',
        result
      );

      if (!response.ok) {
        throw new Error(
          result?.message ||
            'Failed to load submissions'
        );
      }

      if (!result?.success) {
        throw new Error(
          result?.message ||
            'Failed to load submissions'
        );
      }

      const qrFields = Array.isArray(
        result?.fields
      )
        ? result.fields
        : [];

      const submissionData = Array.isArray(
        result?.data
      )
        ? result.data
        : [];

      setFields(qrFields);
      setResponses(submissionData);

    } catch (err) {
      console.error(
        '❌ Load submissions error:',
        err
      );

      setError(
        err?.message ||
          'Failed to load submissions'
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // AUTO SELECT FIRST QR
  // ============================================

  useEffect(() => {
    if (
      autoSelectFirst &&
      qrList.length > 0 &&
      !selectedQR
    ) {
      loadSubmissions(qrList[0]);
    }
  }, [
    autoSelectFirst,
    qrList
  ]);

  // ============================================
  // FILTER QR
  // ============================================

  const filteredQRs = useMemo(() => {
    const term = search
      .trim()
      .toLowerCase();

    if (!term) {
      return qrList;
    }

    return qrList.filter((qr) =>
      getQRName(qr)
        .toLowerCase()
        .includes(term)
    );
  }, [qrList, search]);

  // ============================================
  // SEARCH SUBMISSIONS
  // ============================================

  const filteredResponses = useMemo(() => {
    const term = responseSearch
      .trim()
      .toLowerCase();

    if (!term) {
      return responses;
    }

    return responses.filter((row) => {
      return fields.some((field) => {
        const value = getFieldValue(
          row,
          field
        );

        return String(
          formatValue(value, field)
        )
          .toLowerCase()
          .includes(term);
      });
    });
  }, [
    responses,
    fields,
    responseSearch
  ]);

  // ============================================
  // PAGINATION
  // ============================================

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredResponses.length /
        ITEMS_PER_PAGE
    )
  );

  const paginatedResponses =
    filteredResponses.slice(
      (currentPage - 1) *
        ITEMS_PER_PAGE,
      currentPage *
        ITEMS_PER_PAGE
    );

  // ============================================
  // BACK
  // ============================================
const handleBack = () => {
  // Parent page वर परत जा
  if (onBack) {
    onBack();
    return;
  }

  // fallback
  setSelectedQR(null);
  setResponses([]);
  setFields([]);
  setError('');
  setSearch('');
  setResponseSearch('');
  setCurrentPage(1);
};

  // ============================================
  // QR LIST
  // ============================================

  if (!selectedQR) {
    return (
      <div style={styles.page}>

        <div style={styles.topBar}>

          <div>
            <div style={styles.eyebrow}>
              FEEDBACK MANAGEMENT
            </div>

            <h1 style={styles.title}>
              View Submissions
            </h1>

            <p style={styles.subtitle}>
              Select a QR code to view
              submitted data.
            </p>
          </div>

          {onBack && (
            <button
              onClick={onBack}
              style={styles.backBtn}
            >
              ← Back
            </button>
          )}

        </div>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        <div style={styles.searchBox}>

          <span style={styles.searchIcon}>
            ⌕
          </span>

          <input
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
            placeholder="Search QR name..."
            style={styles.searchInput}
          />

        </div>

        <div style={styles.card}>

          {filteredQRs.length === 0 ? (
            <EmptyState
              title="No QR codes found"
              text="There are no QR codes available."
            />
          ) : (

            <div style={styles.tableWrap}>

              <table style={styles.table}>

                <thead>
                  <tr>

                    <th style={styles.th}>
                      #
                    </th>

                    <th style={styles.th}>
                      QR Name
                    </th>

                    <th style={styles.th}>
                      Responses
                    </th>

                    <th style={styles.th}>
                      Action
                    </th>

                  </tr>
                </thead>

                <tbody>

                  {filteredQRs.map(
                    (qr, index) => {

                      const count =
                        qr?.total_submissions ??
                        qr?.response_count ??
                        qr?.responses_count ??
                        0;

                      return (
                        <tr
                          key={
                            getQRId(qr) ||
                            index
                          }
                        >

                          <td
                            style={
                              styles.tdMuted
                            }
                          >
                            {index + 1}
                          </td>

                          <td
                            style={
                              styles.tdStrong
                            }
                          >

                            <div
                              style={
                                styles.qrName
                              }
                            >
                              {getQRName(qr)}
                            </div>

                            <div
                              style={
                                styles.qrId
                              }
                            >
                              ID:{' '}
                              {getQRId(qr) ||
                                '-'}
                            </div>

                          </td>

                          <td
                            style={styles.td}
                          >
                            <span
                              style={
                                styles.badge
                              }
                            >
                              {count}
                            </span>
                          </td>

                          <td
                            style={styles.td}
                          >

                            <button
                              style={
                                styles.viewBtn
                              }
                              onClick={() =>
                                loadSubmissions(
                                  qr
                                )
                              }
                            >
                              View Submissions →
                            </button>

                          </td>

                        </tr>
                      );
                    }
                  )}

                </tbody>

              </table>

            </div>

          )}

        </div>

      </div>
    );
  }

  // ============================================
  // SELECTED QR
  // ============================================

  return (
    <div style={styles.page}>

      <button
        onClick={handleBack}
        style={styles.backBtn}
      >
        ← Back to QR Codes
      </button>

      <div style={styles.topBar}>

        <div>

          <div style={styles.eyebrow}>
            QR SUBMISSIONS
          </div>

          <h1 style={styles.title}>
            {getQRName(selectedQR)}
          </h1>

          <p style={styles.subtitle}>
            User submitted data for this QR.
          </p>

        </div>

        <button
          style={styles.refreshBtn}
          onClick={() =>
            loadSubmissions(selectedQR)
          }
        >
          ↻ Refresh
        </button>

      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {/* ========================================
          STATS
      ======================================== */}

      <div style={styles.stats}>

        <div style={styles.statCard}>

          <span style={styles.statLabel}>
            TOTAL SUBMISSIONS
          </span>

          <strong style={styles.statValue}>
            {responses.length}
          </strong>

        </div>

        <div style={styles.statCard}>

          <span style={styles.statLabel}>
            FORM FIELDS
          </span>

          <strong style={styles.statValue}>
            {fields.length}
          </strong>

        </div>

        <div style={styles.statCard}>

          <span style={styles.statLabel}>
            QR ID
          </span>

          <strong style={styles.statQR}>
            {getQRId(selectedQR)}
          </strong>

        </div>

      </div>

      {/* ========================================
          SEARCH
      ======================================== */}

      <div style={styles.searchRow}>

        <div
          style={{
            ...styles.searchBox,
            marginBottom: 0,
            flex: 1
          }}
        >

          <span style={styles.searchIcon}>
            ⌕
          </span>

          <input
            value={responseSearch}
            onChange={(e) => {
              setResponseSearch(
                e.target.value
              );
              setCurrentPage(1);
            }}
            placeholder="Search submitted data..."
            style={styles.searchInput}
          />

        </div>

        <div style={styles.resultCount}>
          {filteredResponses.length}{' '}
          result
          {filteredResponses.length === 1
            ? ''
            : 's'}
        </div>

      </div>

      {/* ========================================
          SUBMISSION TABLE
      ======================================== */}

      <div style={styles.card}>

        {loading ? (

          <Loading
            text="Loading submissions..."
          />

        ) : responses.length === 0 ? (

          <EmptyState
            title="No submissions yet"
            text="No data has been submitted for this QR code."
          />

        ) : fields.length === 0 ? (

          <EmptyState
            title="No fields found"
            text="This QR does not have any form fields."
          />

        ) : filteredResponses.length === 0 ? (

          <EmptyState
            title="No matching submissions"
            text="Try another search."
          />

        ) : (

          <>

            <div style={styles.tableWrap}>

              <table
                style={{
                  ...styles.table,
                  minWidth:
                    180 +
                    fields.length * 180
                }}
              >

                {/* =================================
                    DYNAMIC HEADER
                ================================= */}

                <thead>

                  <tr>

                    <th style={styles.th}>
                      #
                    </th>

                    {fields.map(
                      (field, index) => (
                        <th
                          key={
                            field?.field_name ||
                            field?.id ||
                            index
                          }
                          style={
                            styles.th
                          }
                        >
                          {getFieldLabel(
                            field
                          )}
                        </th>
                      )
                    )}

                    <th style={styles.th}>
                      Submitted
                    </th>

                  </tr>

                </thead>

                {/* =================================
                    DATA ROWS
                ================================= */}

                <tbody>

                  {paginatedResponses.map(
                    (row, index) => {

                      const globalIndex =
                        (currentPage - 1) *
                          ITEMS_PER_PAGE +
                        index;

                      return (

                        <tr
                          key={
                            getSubmissionId(
                              row
                            ) ||
                            globalIndex
                          }
                        >

                          {/* NUMBER */}

                          <td
                            style={
                              styles.tdMuted
                            }
                          >
                            {globalIndex + 1}
                          </td>

                          {/* =================================
                              QR FIELD DATA
                          ================================= */}

                          {fields.map(
                            (
                              field,
                              fieldIndex
                            ) => {

                              const value =
                                getFieldValue(
                                  row,
                                  field
                                );

                              const description =
                                row?.[
                                  `${field.field_name}_description`
                                ];

                              return (

                                <td
                                  key={
                                    field?.field_name ||
                                    fieldIndex
                                  }
                                  style={
                                    styles.td
                                  }
                                >

                                  <div
                                    style={
                                      styles.value
                                    }
                                  >
                                    {formatValue(
                                      value,
                                      field
                                    )}
                                  </div>

                                  {field?.field_type ===
                                    'rating' &&
                                    description && (
                                      <div
                                        style={
                                          styles.description
                                        }
                                      >
                                        {description}
                                      </div>
                                    )}

                                </td>

                              );
                            }
                          )}

                          {/* DATE */}

                          <td
                            style={
                              styles.td
                            }
                          >

                            {formatDate(
                              row?.created_at ||
                                row?.createdAt ||
                                row?.submitted_at
                            )}

                          </td>

                        </tr>

                      );
                    }
                  )}

                </tbody>

              </table>

            </div>

            {/* ========================================
                PAGINATION
            ======================================== */}

            <div
              style={
                styles.pagination
              }
            >

              <button
                style={{
                  ...styles.pageBtn,
                  ...(currentPage === 1
                    ? styles.disabledBtn
                    : {})
                }}
                disabled={
                  currentPage === 1
                }
                onClick={() =>
                  setCurrentPage((p) =>
                    Math.max(1, p - 1)
                  )
                }
              >
                ← Previous
              </button>

              <div
                style={
                  styles.pageInfo
                }
              >
                Page{' '}
                <strong>
                  {currentPage}
                </strong>{' '}
                of{' '}
                <strong>
                  {totalPages}
                </strong>
              </div>

              <button
                style={{
                  ...styles.pageBtn,
                  ...(currentPage ===
                  totalPages
                    ? styles.disabledBtn
                    : {})
                }}
                disabled={
                  currentPage ===
                  totalPages
                }
                onClick={() =>
                  setCurrentPage((p) =>
                    Math.min(
                      totalPages,
                      p + 1
                    )
                  )
                }
              >
                Next →
              </button>

            </div>

          </>

        )}

      </div>

    </div>
  );
};

// ============================================
// LOADING
// ============================================

const Loading = ({ text }) => (
  <div style={styles.loading}>

    <div style={styles.spinner} />

    <p>{text}</p>

  </div>
);

// ============================================
// EMPTY
// ============================================

const EmptyState = ({
  title,
  text
}) => (
  <div style={styles.empty}>

    <div style={styles.emptyIcon}>
      📭
    </div>

    <h3 style={styles.emptyTitle}>
      {title}
    </h3>

    <p style={styles.emptyText}>
      {text}
    </p>

  </div>
);

// ============================================
// STYLES
// ============================================

const styles = {

  page: {
    minHeight: '100vh',
    padding: '30px',
    background: '#f5f7fb',
    boxSizing: 'border-box'
  },

  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
    marginBottom: 24
  },

  eyebrow: {
    color: '#64748b',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '.12em',
    marginBottom: 7
  },

  title: {
    margin: 0,
    color: '#172033',
    fontSize: 28,
    fontWeight: 800
  },

  subtitle: {
    margin: '7px 0 0',
    color: '#718096',
    fontSize: 14
  },

  backBtn: {
    border: 0,
    background: 'transparent',
    color: '#5165d8',
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    marginBottom: 18
  },

  refreshBtn: {
    border: '1px solid #d9e0ea',
    background: '#fff',
    color: '#334155',
    borderRadius: 9,
    padding: '10px 15px',
    fontWeight: 700,
    cursor: 'pointer'
  },

  searchBox: {
    width: 420,
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    background: '#fff',
    border: '1px solid #e1e7ef',
    borderRadius: 10,
    padding: '0 14px',
    marginBottom: 18,
    boxSizing: 'border-box'
  },

  searchIcon: {
    color: '#64748b',
    fontSize: 20
  },

  searchInput: {
    width: '100%',
    border: 0,
    outline: 0,
    padding: '12px 4px',
    fontSize: 14,
    background: 'transparent'
  },

  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 15,
    marginBottom: 18
  },

  resultCount: {
    color: '#64748b',
    fontSize: 13,
    whiteSpace: 'nowrap'
  },

  card: {
    background: '#fff',
    border: '1px solid #e4e9f0',
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow:
      '0 5px 20px rgba(30,45,70,.04)'
  },

  tableWrap: {
    width: '100%',
    overflowX: 'auto'
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },

  th: {
    textAlign: 'left',
    padding: '15px 18px',
    background: '#f8fafc',
    color: '#64748b',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '.05em',
    borderBottom:
      '1px solid #e6ebf1',
    whiteSpace: 'nowrap'
  },

  td: {
    padding: '15px 18px',
    color: '#4b5563',
    fontSize: 13,
    verticalAlign: 'top',
    borderBottom:
      '1px solid #edf1f5',
    minWidth: 160
  },

  tdMuted: {
    padding: '15px 18px',
    color: '#94a3b8',
    fontSize: 13,
    verticalAlign: 'top',
    borderBottom:
      '1px solid #edf1f5'
  },

  tdStrong: {
    padding: '15px 18px',
    color: '#1f2937',
    fontSize: 13,
    fontWeight: 750,
    verticalAlign: 'middle',
    borderBottom:
      '1px solid #edf1f5'
  },

  qrName: {
    fontWeight: 750,
    color: '#1f2937'
  },

  qrId: {
    marginTop: 4,
    fontSize: 11,
    color: '#94a3b8'
  },

  badge: {
    display: 'inline-flex',
    minWidth: 34,
    height: 28,
    padding: '0 9px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 14,
    background: '#eef2ff',
    color: '#4f46c5',
    fontWeight: 800
  },

  viewBtn: {
    border: 0,
    background: '#5165d8',
    color: '#fff',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 750,
    cursor: 'pointer'
  },

  stats: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(3, minmax(0, 1fr))',
    gap: 15,
    marginBottom: 20
  },

  statCard: {
    background: '#fff',
    border: '1px solid #e4e9f0',
    borderRadius: 12,
    padding: '18px 20px'
  },

  statLabel: {
    display: 'block',
    color: '#94a3b8',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '.08em',
    marginBottom: 7
  },

  statValue: {
    color: '#172033',
    fontSize: 24
  },

  statQR: {
    color: '#5165d8',
    fontSize: 16
  },

  value: {
    color: '#334155',
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxWidth: 300
  },

  description: {
    marginTop: 7,
    paddingTop: 7,
    borderTop: '1px solid #edf1f5',
    color: '#64748b',
    fontSize: 12,
    lineHeight: 1.45
  },

  error: {
    background: '#fff5f5',
    border: '1px solid #fecaca',
    color: '#991b1b',
    borderRadius: 10,
    padding: '14px 18px',
    marginBottom: 18
  },

  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 18px',
    borderTop:
      '1px solid #edf1f5'
  },

  pageBtn: {
    border: '1px solid #d9e0ea',
    background: '#fff',
    color: '#334155',
    borderRadius: 8,
    padding: '8px 13px',
    fontWeight: 700,
    cursor: 'pointer'
  },

  disabledBtn: {
    opacity: 0.45,
    cursor: 'not-allowed'
  },

  pageInfo: {
    color: '#64748b',
    fontSize: 13
  },

  loading: {
    padding: 70,
    textAlign: 'center',
    color: '#718096'
  },

  spinner: {
    width: 34,
    height: 34,
    margin: '0 auto 12px',
    border: '4px solid #e5e7eb',
    borderTop:
      '4px solid #5165d8',
    borderRadius: '50%',
    animation:
      'qrSubmissionSpin .8s linear infinite'
  },

  empty: {
    padding: '70px 30px',
    textAlign: 'center',
    color: '#718096'
  },

  emptyIcon: {
    fontSize: 42,
    marginBottom: 8
  },

  emptyTitle: {
    margin: '0 0 6px',
    color: '#334155'
  },

  emptyText: {
    margin: 0,
    color: '#94a3b8'
  }
};

// ============================================
// SPINNER
// ============================================

if (
  typeof document !== 'undefined' &&
  !document.getElementById(
    'qr-submission-spin-style'
  )
) {
  const style =
    document.createElement('style');

  style.id =
    'qr-submission-spin-style';

  style.textContent = `
    @keyframes qrSubmissionSpin {
      to {
        transform: rotate(360deg);
      }
    }
  `;

  document.head.appendChild(style);
}

export default QRSubmissions;